import React from "react";
import { Box, Typography } from "@mui/material";
import ButtonNavLink from "../Buttons/ButtonNavLink";
import sleepAfinando from "../../assets/images/sleepAfinando.svg";
import * as kRoutes from "../../constants/routes";
import { useTranslation } from "react-i18next";

/**
 * EmptyState - Componente reutilizável para exibir estados vazios
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.text - Texto principal a ser exibido (OBRIGATÓRIO)
 * @param {string} [props.image=sleepAfinando] - URL ou import da imagem (opcional)
 * @param {string} [props.altText="Nenhuma jogada encontrada"] - Texto alternativo da imagem (opcional)
 * @param {boolean} [props.showButton=false] - Exibe botão para ir aos jogos (opcional)
 * @param {string|ReactNode} [props.description=null] - Texto secundário abaixo do título (opcional)
 * 
 * @example
 * // Uso básico (apenas texto)
 * <EmptyState text="Nenhum item encontrado" />
 * 
 * @example
 * // Com botão habilitado
 * <EmptyState 
 *   text="Você ainda não fez nenhuma jogada"
 *   showButton={true}
 * />
 * 
 * @example
 * // Com imagem personalizada
 * import minhaImagem from "../assets/imagem.svg";
 * <EmptyState 
 *   image={minhaImagem}
 *   altText="Descrição da minha imagem"
 *   text="Nenhum resultado encontrado"
 *   showButton={true}
 * />
 */

const EmptyState = React.memo(({
  image = sleepAfinando,
  altText = "Nenhuma jogada encontrada",
  text,
  description = null,
  showButton = false
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        mt: { xs: 0, md: "0px" },
        width: "100%",
        height: { xs: "50svh", sm: "50dvh" },
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          justifyContent: showButton ? "space-between" : "center",
          gap: showButton ? 0 : 1,
          width: { xs: "95%", md: "80%", },
          maxWidth: "459px",
          height: "50vh",
          maxHeight: "315px",
          "& img": {
            width: "150px",
          },
        }}
      >
        <img src={image} alt={altText} />
        <Typography
          sx={{
            fontSize: { xs: "14px", sm: "18px" },
            color: "#662d91",
            fontWeight: "bold",
          }}
        >
          {text}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#666",
              textAlign: "center"
            }}
          >
            {description}
          </Typography>
        )}
        {showButton && (
          <ButtonNavLink
            sx={{ fontSize: { xs: "14px", md: "16px" } }}
            to={kRoutes.R_ACTIVTS}
            text={t("escolher um jogo")}
          />
        )}
      </Box>
    </Box>
  );
});

EmptyState.displayName = "EmptyState";

export default EmptyState;